import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useQueryClient, useQuery as useTanstackQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import qs from 'qs';
import { Helmet } from 'react-helmet';

import { Box, Container } from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import usePromoMeta from '~/components/Promo/hooks/usePromoMeta';

import { useDeleteVoucher } from '~/hooks/apis/promo/useDeleteVoucher';
import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentTenant from '~/hooks/useCurrentTenant';
import useDebounced from '~/hooks/useDebounced';
import useQuery from '~/hooks/useQuery';

import { ListVouchersQuery, ListVouchersResponse, PromoMeta, Voucher, listVouchers } from '~/services/PromoService';
import UsersService from '~/services/UsersService';

import VouchersTab from './VouchersTab';
import { parseCaseIdFilterQueryParam, serializeCaseIdFilterForApi } from './caseIdFilterTokens';

type VoucherReportingTagDefinition = NonNullable<
  PromoMeta['global_promo_config']
>['voucherReportingTagsConfig'][number];
type ListVouchersQueryWithReportingTags = ListVouchersQuery & { voucher_reporting_tags?: Array<string> };

function parseReportingTagsQueryParam(value: string | null): Array<string> {
  if (!value) {
    return [];
  }
  return value
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
}

function reportingTagOptionsFromPromoMeta(
  config: Array<VoucherReportingTagDefinition> | undefined,
): Array<{ value: string; label: string }> {
  if (!config) {
    return [];
  }
  return config.map((def) => ({
    value: def.tag,
    label: def.short_description?.trim() ? def.short_description : def.tag,
  }));
}

export default function VouchersContainer() {
  const query = useQuery();
  const { push } = useAppNavigation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const tenant = useCurrentTenant();
  const { promoMeta } = usePromoMeta();
  const deleteVoucherMutation = useDeleteVoucher();

  const [pageNum, setPageNum] = useState<number>(query.get('page_num') ? Number(query.get('page_num')) : 0);
  const [pageSize, setPageSize] = useState<number>(query.get('page_size') ? Number(query.get('page_size')) : 20);
  const [voucherName, setVoucherName] = useState<string>(query.get('voucher_name') || '');
  const [selectedReportingTags, setSelectedReportingTags] = useState<Array<string>>(() =>
    parseReportingTagsQueryParam(query.get('voucher_reporting_tags')),
  );
  const [caseIdFilter, setCaseIdFilter] = useState<Array<string>>(() =>
    parseCaseIdFilterQueryParam(query.get('case_id')),
  );
  const debouncedCaseIdFilter = useDebounced(caseIdFilter, 400);
  const [userEmail, setUserEmail] = useState<string>(query.get('user_email') || '');
  const [isActive, setIsActive] = useState<'all' | 'active' | 'inactive'>(() => {
    const activeParam = query.get('is_active');
    if (activeParam === 'active' || activeParam === 'inactive') {
      return activeParam;
    }
    return 'all';
  });
  const [onlyActive, setOnlyActive] = useState<boolean>(() => {
    if (parseCaseIdFilterQueryParam(query.get('case_id')).length > 0) {
      return false;
    }
    return query.get('only_active') !== 'false';
  });

  const caseIdFilterApi = useMemo(() => serializeCaseIdFilterForApi(caseIdFilter), [caseIdFilter]);
  const caseIdFilterApiForQuery = useMemo(
    () => serializeCaseIdFilterForApi(debouncedCaseIdFilter),
    [debouncedCaseIdFilter],
  );
  const hasCaseIdFilter = caseIdFilter.length > 0;
  const hasCaseIdFilterForQuery = debouncedCaseIdFilter.length > 0;
  const effectiveOnlyActive = hasCaseIdFilter ? false : onlyActive;
  const isCaseIdFilterDebouncing = caseIdFilterApi !== caseIdFilterApiForQuery;

  useEffect(() => {
    if (hasCaseIdFilter) {
      setOnlyActive(false);
    }
  }, [hasCaseIdFilter]);

  const isActiveParam = isActive === 'active' ? true : isActive === 'inactive' ? false : undefined;
  const normalizedEmail = userEmail.trim().toLowerCase();
  const reportingTagOptions = useMemo(
    () => reportingTagOptionsFromPromoMeta(promoMeta?.global_promo_config?.voucherReportingTagsConfig),
    [promoMeta?.global_promo_config?.voucherReportingTagsConfig],
  );

  const { data: userMatches = [] } = useTanstackQuery({
    queryKey: ['voucher-user-search', normalizedEmail, tenant.tenant.brand],
    queryFn: async () => {
      if (!normalizedEmail) {
        return [];
      }
      return await UsersService.getUsers({ filter: normalizedEmail, limit: 10, brand: tenant.tenant.brand });
    },
    enabled: Boolean(normalizedEmail?.includes('@')),
    refetchOnWindowFocus: false,
  });

  const resolvedUserId = useMemo(() => {
    if (!normalizedEmail) {
      return undefined;
    }
    const exact = (userMatches as Array<{ id_member?: string; email?: string; email_address?: string }>).find(
      (user) => {
        const email = user.email || user.email_address || '';
        return email.toLowerCase() === normalizedEmail;
      },
    );
    return exact?.id_member;
  }, [normalizedEmail, userMatches]);

  const queryParams: ListVouchersQueryWithReportingTags = useMemo(() => {
    return {
      page: pageNum,
      limit: pageSize,
      brand: tenant.tenant.brand,
      only_active: effectiveOnlyActive,
      ...(voucherName ? { voucher_name: voucherName } : {}),
      ...(selectedReportingTags.length > 0 ? { voucher_reporting_tags: selectedReportingTags } : {}),
      ...(hasCaseIdFilterForQuery ? { case_id_filter: caseIdFilterApiForQuery } : {}),
      ...(resolvedUserId ? { user_id: resolvedUserId } : {}),
      ...(isActiveParam !== undefined ? { is_active: isActiveParam } : {}),
    } as ListVouchersQueryWithReportingTags;
  }, [
    pageNum,
    pageSize,
    voucherName,
    selectedReportingTags,
    caseIdFilterApiForQuery,
    hasCaseIdFilterForQuery,
    resolvedUserId,
    isActiveParam,
    effectiveOnlyActive,
    tenant.tenant.brand,
  ]);

  const setQueryParams = useCallback(() => {
    const searchParams = qs.stringify({
      page_num: pageNum.toString(),
      page_size: pageSize.toString(),
      voucher_name: voucherName || undefined,
      voucher_reporting_tags: selectedReportingTags.length > 0 ? selectedReportingTags.join(',') : undefined,
      brand: tenant.tenant.brand,
      case_id: hasCaseIdFilter ? caseIdFilter.join(',') : undefined,
      user_email: userEmail || undefined,
      is_active: isActive,
      ...(!effectiveOnlyActive ? { only_active: 'false' } : {}),
    });
    push({ search: `?${searchParams}` });
  }, [
    push,
    pageNum,
    pageSize,
    voucherName,
    selectedReportingTags,
    hasCaseIdFilter,
    caseIdFilter,
    userEmail,
    isActive,
    effectiveOnlyActive,
    tenant.tenant.brand,
  ]);

  const shouldFetchVouchers = !normalizedEmail || Boolean(resolvedUserId);
  const { data, isLoading, isFetching } = useTanstackQuery<ListVouchersResponse>({
    queryKey: [
      'vouchers',
      {
        pageNum,
        pageSize,
        voucherName,
        selectedReportingTags,
        caseIdFilterApiForQuery,
        userEmail,
        resolvedUserId,
        isActive,
        effectiveOnlyActive,
        brand: tenant.tenant.brand,
      },
    ],
    queryFn: async () => {
      const res = await listVouchers(queryParams);
      setQueryParams();
      return res;
    },
    refetchOnWindowFocus: false,
    enabled: shouldFetchVouchers,
  });

  const onPageChange = useCallback(
    (page: number, pageSize: number) => {
      setPageNum(page);
      setPageSize(pageSize);
      query.set('page_num', page.toString());
      query.set('page_size', pageSize.toString());
      query.set('voucher_name', voucherName);
      if (selectedReportingTags.length > 0) {
        query.set('voucher_reporting_tags', selectedReportingTags.join(','));
      } else {
        query.delete('voucher_reporting_tags');
      }
      if (hasCaseIdFilter) {
        query.set('case_id', caseIdFilter.join(','));
      } else {
        query.delete('case_id');
      }
      query.set('user_email', userEmail);
      if (isActive === 'all') {
        query.delete('is_active');
      } else {
        query.set('is_active', isActive);
      }
      if (effectiveOnlyActive) {
        query.delete('only_active');
      } else {
        query.set('only_active', 'false');
      }
      window.history.pushState({}, '', `${window.location.pathname}?${query.toString()}`);
    },
    [
      query,
      voucherName,
      selectedReportingTags,
      hasCaseIdFilter,
      caseIdFilter,
      userEmail,
      isActive,
      effectiveOnlyActive,
    ],
  );

  const vouchers = shouldFetchVouchers ? (data?.result?.vouchers ?? []) : [];
  const totalCount = shouldFetchVouchers ? Number(data?.result?.total ?? 0) : 0;

  const handleDeleteVoucher = useCallback(
    async (voucher: Voucher) => {
      if (!voucher.voucherId) {
        enqueueSnackbar('Unable to delete voucher: missing voucher id.', { variant: 'error' });
        throw new Error('Missing voucher id');
      }
      try {
        await deleteVoucherMutation.mutateAsync({ voucherId: voucher.voucherId });
        enqueueSnackbar(`Deleted voucher ${voucher.voucherName || voucher.voucherId}.`, { variant: 'success' });
        queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      } catch (error) {
        enqueueSnackbar(`Failed to delete voucher: ${(error as Error).message}`, { variant: 'error' });
        throw error;
      }
    },
    [deleteVoucherMutation, enqueueSnackbar, queryClient],
  );

  return (
    <Container maxWidth="xl" sx={{ width: '90%' }}>
      <Helmet>
        <title>Vouchers</title>
      </Helmet>
      <Box>
        <PageSubheader title={`Vouchers [${vouchers.length} of ${totalCount}]`} />
        <VouchersTab
          voucherName={voucherName}
          setVoucherName={setVoucherName}
          reportingTagOptions={reportingTagOptions}
          selectedReportingTags={selectedReportingTags}
          setSelectedReportingTags={setSelectedReportingTags}
          caseIdFilter={caseIdFilter}
          setCaseIdFilter={setCaseIdFilter}
          userEmail={userEmail}
          setUserEmail={setUserEmail}
          effectiveOnlyActive={effectiveOnlyActive}
          caseIdFilterActive={hasCaseIdFilter}
          setOnlyActive={setOnlyActive}
          data={vouchers}
          isLoading={isLoading || isFetching || isCaseIdFilterDebouncing}
          isDeleting={deleteVoucherMutation.isPending}
          totalCount={totalCount}
          pageNum={pageNum}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onConfirmDelete={handleDeleteVoucher}
        />
      </Box>
    </Container>
  );
}
